/**
 * Sales Workflow Stages Master — read-only documentation page.
 *
 * Single source of truth for every stage / status that can show up on a sale
 * across all departments. Pulled from the live backend logic (see references
 * inline) so it stays accurate without duplicating computation.
 */
import React, { useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { useApp } from "../context/AppContext";
import { Lock, Search } from "lucide-react";

// ──────────────────────────────────────────────────────────────────────────────
// Master dataset
// ──────────────────────────────────────────────────────────────────────────────

const TONE_STYLES = {
  grey:   "bg-slate-100 text-slate-700 border-slate-200",
  blue:   "bg-blue-100 text-blue-700 border-blue-200",
  green:  "bg-emerald-100 text-emerald-700 border-emerald-200",
  yellow: "bg-amber-100 text-amber-800 border-amber-200",
  orange: "bg-orange-100 text-orange-800 border-orange-200",
  red:    "bg-rose-100 text-rose-700 border-rose-200",
  purple: "bg-violet-100 text-violet-700 border-violet-200",
};

const STAGES = [
  // ─── Sales lifecycle ──────────────────────────────────────────────────
  { group: "Sales Lifecycle", code: "draft", label: "Draft",
    when: "Sales Form saved via 'Save as Draft' before final release.",
    trigger: "Sale created with status='draft'. Visible only to owning DSE, Sales Manager & Admin.",
    owner: "DSE",
    next: "Sales Released",
    tone: "grey" },
  { group: "Sales Lifecycle", code: "submitted", label: "Sales Released",
    when: "Sales Form submitted/released; SR number generated.",
    trigger: "POST /api/sales/{id}/submit → status becomes 'submitted', WhatsApp welcome_one_desk360 fires.",
    owner: "DSE → All",
    next: "In Progress",
    tone: "blue" },
  { group: "Sales Lifecycle", code: "in_progress", label: "In Progress",
    when: "Workflow tasks created and being executed.",
    trigger: "Auto-set by _refresh_sale_status once any task exists and not yet ready_for_delivery.",
    owner: "System",
    next: "Ready for Delivery",
    tone: "blue" },
  { group: "Sales Lifecycle", code: "ready_for_delivery", label: "Delivery Ready",
    when: "All blocking tasks for delivery are completed.",
    trigger: "Refresh logic flips status when delivery-blockers (Accounts, Insurance, Registration, PDI, Spares, etc.) are all done.",
    owner: "System",
    next: "Delivered",
    tone: "green" },
  { group: "Sales Lifecycle", code: "delivered", label: "Delivered",
    when: "Vehicle handed over to customer.",
    trigger: "POST /api/sales/{id}/deliver → status='delivered'. WhatsApp thanks_for_the_purchase fires.",
    owner: "DSE",
    next: "(terminal)",
    tone: "green" },

  // ─── Side branches ────────────────────────────────────────────────────
  { group: "Side Branches", code: "transit_hold", label: "Transit Hold",
    when: "Vehicle sold before it has physically arrived at the dealership.",
    trigger: "POST /api/sales/{id}/transit/hold. Pauses SLA timers; back-office processes paused until release.",
    owner: "DSE / Admin",
    next: "Resumes previous in-flight status",
    tone: "blue" },
  { group: "Side Branches", code: "early_delivery_requested", label: "Early Delivery Requested",
    when: "DSE has requested a conditional delivery before all docs are ready.",
    trigger: "POST /api/sales/{id}/early_delivery/request → early_delivery_status='pending'.",
    owner: "DSE",
    next: "Approved / Rejected",
    tone: "yellow" },
  { group: "Side Branches", code: "early_delivery_approved", label: "Early Delivery Approved",
    when: "Accounts/Admin has approved the early delivery request.",
    trigger: "POST /api/sales/{id}/early_delivery/approve → early_delivery_status='approved'.",
    owner: "Accounts/Admin",
    next: "Delivered (Conditional)",
    tone: "purple" },
  { group: "Side Branches", code: "delivered_conditional", label: "Delivered (Conditional)",
    when: "Sale was marked delivered after Early Delivery approval — paperwork still pending.",
    trigger: "Same delivered flow, but early_delivery_status='approved' is preserved.",
    owner: "DSE",
    next: "Open Conditional Items closure",
    tone: "purple" },
  { group: "Side Branches", code: "delayed", label: "Delayed",
    when: "SLA timer breached on any open task.",
    trigger: "Computed by SLA engine — task.due_at < now and status='pending'. Surfaces as red row pill.",
    owner: "System",
    next: "Mitigate / escalate",
    tone: "red" },
  { group: "Side Branches", code: "sla_risk", label: "SLA Risk",
    when: "Task due within the risk window before breach.",
    trigger: "SLA engine flags due_in < risk_threshold. Yellow pill on row.",
    owner: "System",
    next: "Delayed (if not actioned)",
    tone: "yellow" },
  { group: "Side Branches", code: "on_hold", label: "On Hold",
    when: "Workflow temporarily paused for a non-transit reason (manual hold).",
    trigger: "Task-level hold via /tasks/{id}/hold. Manager intervention required to release.",
    owner: "Sales Manager / Admin",
    next: "Resume",
    tone: "orange" },

  // ─── PDI ──────────────────────────────────────────────────────────────
  { group: "PDI", code: "pdi_vin_pending", label: "VIN Assignment Pending",
    when: "Sale released but no VIN/Engine assigned yet.",
    trigger: "Task type='PDI_VIN' status='pending'.",
    owner: "PDI",
    next: "VIN Assigned",
    tone: "yellow" },
  { group: "PDI", code: "pdi_vin_done", label: "VIN Assigned",
    when: "PDI has tagged the chassis with the sale.",
    trigger: "PDI_VIN task completed via POST /sales/{id}/pdi/release_for_processing.",
    owner: "PDI",
    next: "Vehicle Readiness",
    tone: "green" },
  { group: "PDI", code: "pdi_ready_pending", label: "Vehicle Readiness Pending",
    when: "Chassis tagged; PDI checklist & photos still pending.",
    trigger: "Task type='PDI_READY' status='pending'.",
    owner: "PDI",
    next: "PDI Complete",
    tone: "yellow" },
  { group: "PDI", code: "pdi_done", label: "PDI Complete",
    when: "All PDI tasks (VIN + Ready) marked done.",
    trigger: "PDI_VIN and PDI_READY tasks status='completed'.",
    owner: "PDI",
    next: "Delivery readiness check",
    tone: "green" },

  // ─── Accounts ─────────────────────────────────────────────────────────
  { group: "Accounts", code: "accounts_pending", label: "Accounts Pending",
    when: "Awaiting invoice + ledger from Accounts.",
    trigger: "Task type='ACCOUNTS_INVOICE' status='pending'.",
    owner: "Accounts",
    next: "Invoice Done",
    tone: "yellow" },
  { group: "Accounts", code: "invoice_pending", label: "Invoice Pending",
    when: "Invoice number / billing ledger not captured yet.",
    trigger: "sale.accounts.invoice_no empty or accounts.billing_ledger_name empty.",
    owner: "Accounts",
    next: "Invoice Done",
    tone: "orange" },
  { group: "Accounts", code: "payment_pending", label: "Payment Pending",
    when: "Required payment amount not received in payment_receipts.",
    trigger: "Sum(payment_receipts.amount) < required cap based on buy mode.",
    owner: "Accounts",
    next: "Payment Done",
    tone: "orange" },
  { group: "Accounts", code: "accounts_done", label: "Accounts Done",
    when: "Invoice generated + ledger set + payment captured.",
    trigger: "ACCOUNTS_INVOICE task status='completed'.",
    owner: "Accounts",
    next: "Back Office Pending",
    tone: "green" },

  // ─── Back Office ──────────────────────────────────────────────────────
  { group: "Back Office", code: "bo_pending", label: "Back Office Pending",
    when: "Accounts complete; back-office tasks not yet picked up.",
    trigger: "Any task of type INSURANCE / ROAD_TAX / HSRP / VAS_PP / VAS_AMC / VAS_RSA in pending.",
    owner: "Back Office",
    next: "Per-department pending status",
    tone: "yellow" },
  { group: "Back Office", code: "insurance_pending", label: "Insurance Pending",
    when: "Insurance task open.",
    trigger: "Task type='INSURANCE' status='pending'.",
    owner: "Back Office",
    next: "Insurance Done",
    tone: "yellow" },
  { group: "Back Office", code: "registration_pending", label: "Registration / RTO Pending",
    when: "Road Tax / Registration not completed.",
    trigger: "Task type='ROAD_TAX' status='pending'.",
    owner: "Back Office",
    next: "Registration Done",
    tone: "yellow" },
  { group: "Back Office", code: "hsrp_pending", label: "HSRP Pending",
    when: "HSRP plate not received / fitted.",
    trigger: "Task type='HSRP' status='pending'.",
    owner: "Back Office",
    next: "HSRP Done",
    tone: "yellow" },
  { group: "Back Office", code: "vas_pp_pending", label: "Protection Plus Pending",
    when: "PP opted but certificate/invoice not uploaded.",
    trigger: "VAS_PP task status='pending'.",
    owner: "Back Office",
    next: "PP Done",
    tone: "yellow" },
  { group: "Back Office", code: "vas_amc_pending", label: "AMC Pending",
    when: "AMC opted; certificate/invoice not uploaded.",
    trigger: "VAS_AMC task status='pending'.",
    owner: "Back Office",
    next: "AMC Done",
    tone: "yellow" },
  { group: "Back Office", code: "vas_rsa_pending", label: "RSA Pending",
    when: "RSA opted; policy doc not uploaded.",
    trigger: "VAS_RSA task status='pending'.",
    owner: "Back Office",
    next: "RSA Done",
    tone: "yellow" },

  // ─── Spares ───────────────────────────────────────────────────────────
  { group: "Spares", code: "accessories_pending", label: "Accessories Pending",
    when: "Accessory items declared on the sale but not yet fitted / invoiced.",
    trigger: "Task type='SPARES' status='pending'.",
    owner: "Spares",
    next: "Spares Done",
    tone: "yellow" },
  { group: "Spares", code: "accessories_not_available", label: "Accessories Not Available",
    when: "Spares team marked one or more accessories not in stock.",
    trigger: "Task type='SPARES' completed with a 'not_available' note.",
    owner: "Spares",
    next: "Procurement / waive",
    tone: "orange" },

  // ─── Terminal & escalation ────────────────────────────────────────────
  { group: "Terminal & Escalation", code: "cancelled", label: "Cancelled",
    when: "Sale cancelled / refunded.",
    trigger: "Sale.is_deleted = true OR explicit cancellation flag (currently mapped via delete endpoint).",
    owner: "Admin",
    next: "(terminal)",
    tone: "red" },
  { group: "Terminal & Escalation", code: "closed", label: "Closed",
    when: "Sale delivered + all paperwork closed (no open conditional items).",
    trigger: "status='delivered' AND no open early-delivery items.",
    owner: "System",
    next: "(terminal)",
    tone: "green" },
];

const TONE_OPTIONS = [
  { value: "all",    label: "All" },
  { value: "grey",   label: "Grey · Draft / Static" },
  { value: "blue",   label: "Blue · Active / Info" },
  { value: "green",  label: "Green · Success" },
  { value: "yellow", label: "Yellow · Pending / Risk" },
  { value: "orange", label: "Orange · Attention" },
  { value: "red",    label: "Red · Breach / Cancelled" },
  { value: "purple", label: "Purple · Conditional" },
];

// ──────────────────────────────────────────────────────────────────────────────
// Component
// ──────────────────────────────────────────────────────────────────────────────

export default function WorkflowStagesMaster() {
  const { activeUser } = useApp();
  const [search, setSearch] = useState("");
  const [tone, setTone] = useState("all");

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return STAGES.filter((s) => {
      if (tone !== "all" && s.tone !== tone) return false;
      if (!q) return true;
      return (
        s.label.toLowerCase().includes(q) ||
        s.when.toLowerCase().includes(q) ||
        s.trigger.toLowerCase().includes(q) ||
        s.owner.toLowerCase().includes(q) ||
        s.group.toLowerCase().includes(q)
      );
    });
  }, [search, tone]);

  // Group rows for visual sectioning.
  const groups = useMemo(() => {
    const out = {};
    for (const row of filtered) {
      (out[row.group] = out[row.group] || []).push(row);
    }
    return out;
  }, [filtered]);

  if (activeUser?.role !== "Admin") {
    return (
      <div className="mx-auto max-w-md py-16 text-center">
        <Lock className="mx-auto h-7 w-7 text-slate-400" />
        <div className="mt-3 text-sm font-semibold text-slate-700">Admin only</div>
        <p className="mt-1 text-xs text-slate-500">
          Sales Workflow Stages Master is restricted to Admin users.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="workflow-stages-page">
      <div>
        <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-slate-500">
          Documentation
        </div>
        <h1 className="mt-1 font-display text-3xl font-semibold tracking-tight">
          Sales Workflow Stages Master
        </h1>
        <p className="mt-1 max-w-3xl text-xs text-slate-500">
          Single source of truth for every stage / status that surfaces on a Sale across all
          departments. Conditions reflect live backend logic — this is documentation only;
          no workflow behaviour is modified here.
        </p>
      </div>

      {/* Filter bar */}
      <Card>
        <CardContent className="flex flex-wrap items-center gap-2 py-3">
          <div className="relative flex-1 min-w-[260px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search stage, condition, owner…"
              className="h-9 pl-8 text-xs"
              data-testid="wfs-search"
            />
          </div>
          <select
            value={tone}
            onChange={(e) => setTone(e.target.value)}
            className="h-9 rounded-md border border-slate-200 bg-white px-2 text-xs font-semibold text-slate-700"
            data-testid="wfs-tone-filter"
          >
            {TONE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <div className="ml-auto text-[11px] uppercase tracking-wider text-slate-500">
            {filtered.length} of {STAGES.length} stages
          </div>
        </CardContent>
      </Card>

      {/* Legend */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-xs font-semibold uppercase tracking-wider text-slate-500">
            Status badge colors
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap items-center gap-2 pt-0">
          {TONE_OPTIONS.filter((o) => o.value !== "all").map((o) => (
            <span
              key={o.value}
              className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${TONE_STYLES[o.value]}`}
            >
              <span className={`h-1.5 w-1.5 rounded-full ${TONE_STYLES[o.value].split(" ")[0]} border ${TONE_STYLES[o.value].split(" ")[2]}`} />
              {o.label}
            </span>
          ))}
        </CardContent>
      </Card>

      {/* Groups */}
      {Object.entries(groups).map(([groupName, rows]) => (
        <Card key={groupName} data-testid={`wfs-group-${groupName}`}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold tracking-tight">
              {groupName}
              <span className="ml-2 text-[11px] font-medium text-slate-400">
                {rows.length} stage{rows.length !== 1 ? "s" : ""}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="px-0 pb-0">
            <div className="overflow-x-auto">
              <table className="w-full text-[11.5px]">
                <thead className="bg-slate-50">
                  <tr className="border-y border-slate-200 text-left text-[10px] uppercase tracking-wider text-slate-500">
                    <th className="px-4 py-2 font-semibold">Stage / Status</th>
                    <th className="px-3 py-2 font-semibold">When it appears</th>
                    <th className="px-3 py-2 font-semibold">Trigger condition</th>
                    <th className="px-3 py-2 font-semibold">Owner</th>
                    <th className="px-3 py-2 font-semibold">Next stage</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr
                      key={row.code}
                      className="border-b border-slate-100 align-top last:border-0"
                      data-testid={`wfs-row-${row.code}`}
                    >
                      <td className="whitespace-nowrap px-4 py-2.5">
                        <span
                          className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${TONE_STYLES[row.tone]}`}
                        >
                          {row.label}
                        </span>
                        <div className="mt-1 font-mono-data text-[10px] text-slate-400">
                          {row.code}
                        </div>
                      </td>
                      <td className="px-3 py-2.5 text-slate-700">{row.when}</td>
                      <td className="px-3 py-2.5 text-slate-600 font-mono-data text-[10.5px]">
                        {row.trigger}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2.5 font-semibold text-slate-700">
                        {row.owner}
                      </td>
                      <td className="px-3 py-2.5 text-slate-600">{row.next}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      ))}

      <p className="text-[11px] italic text-slate-400">
        Audit-grade reference. Any change to the underlying workflow code (server.py status
        refresh, task creation, SLA computation) should be mirrored here so this page stays
        the single source of truth.
      </p>
    </div>
  );
}
